const googleMaps = (query) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;

const osmEmbed = (lat, lon, delta = 0.018) => {
  const minLon = lon - delta;
  const minLat = lat - delta * 0.65;
  const maxLon = lon + delta;
  const maxLat = lat + delta * 0.65;
  return `https://www.openstreetmap.org/export/embed.html?bbox=${minLon},${minLat},${maxLon},${maxLat}&layer=mapnik&marker=${lat},${lon}`;
};

const TRAILHEAD_QUERY =
  'Rocks and Roots Trail Trailhead, 3993 Hollenback Rd, Lewis Center, OH 43035';

const CREW_PHOTO = '/blog/alum-creek-hiking-trail-crew.jpg';
const HIKER_PHOTO = '/blog/alum-creek-trail-hiker.jpg';
const SUNRISE_PHOTO = '/blog/alum-creek-sunrise-lake.jpg';
const TRAIL_CLIP = '/blog/alum-creek-trail-clip.mp4';
const TRAIL_CLIP_POSTER = '/blog/alum-creek-trail-clip-poster.jpg';

export const ROCKS_AND_ROOTS_MORNING_HIKE_RECAP_SLUG =
  'rocks-and-roots-alum-creek-morning-hike-recap';

/**
 * Friends-group recap of the planned 7:00 a.m. Rocks and Roots hike at Alum Creek.
 */
export const ROCKS_AND_ROOTS_MORNING_HIKE_RECAP_POST = {
  id: 'featured-rocks-and-roots-morning-hike-recap',
  slug: ROCKS_AND_ROOTS_MORNING_HIKE_RECAP_SLUG,
  title: 'Rocks and Roots Recap: Alum Creek Morning Hike (7:00–9:30 a.m.)',
  excerpt:
    'We met at 7:00 a.m. as planned and were back at the Alum Creek trailhead by 9:30. Crew photo, sunrise on the lake, a stream-crossing clip from the woods, and how the 2.5-hour loop actually went.',
  publishedAt: '2026-09-08T14:00:00.000Z',
  coverImageUrl: CREW_PHOTO,
  authorName: 'Daily Sports Life',
  clubName: 'Ohio Trail Guide',
  blogCategory: 'club-news',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'exercise',
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Hike recap snapshot',
      items: [
        { value: '7:00 a.m.', label: 'Started as planned' },
        { value: '9:30 a.m.', label: 'Back at the trailhead' },
        { value: '2.5 hr', label: 'On our feet' },
        { value: '7 hikers', label: 'Friends-group crew' },
      ],
    },
    {
      type: 'panel',
      title: 'We showed up — and finished on time',
      paragraphs: [
        'The [7:00 a.m. Rocks and Roots plan](/blog/rocks-and-roots-alum-creek-morning-hike-plan) was not theoretical this time. Seven of us met at the **Alum Creek Hiking Trail** kiosk on Hollenback Road, took the group photo, and started walking.',
        'We were **done by 9:30 a.m.** — right in the window we wrote down (cars back about 9:15–9:45). No late start, no soaked-clay cancel. A 2.5-hour morning on dirt, then the day still had room for coffee and the rest of life.',
      ],
      chips: ['Alum Creek State Park', 'Rocks and Roots', 'Lewis Center, OH', 'Friends group'],
    },
    {
      type: 'imageGallery',
      layout: 'hero',
      title: 'The crew at the trailhead',
      intro:
        'Seven of us at the Alum Creek Hiking Trail kiosk — trekking poles, trail shoes, and a 7:00 a.m. start. This is the photo from the lot before we headed into the woods.',
      images: [
        {
          url: CREW_PHOTO,
          alt: 'Seven friends in hiking gear smiling at the Alum Creek Hiking Trail kiosk trailhead',
          caption:
            'The group at the trailhead kiosk. Poles, packs, and a paved lot with the woods right behind us.',
          mapUrl: googleMaps(TRAILHEAD_QUERY),
        },
      ],
    },
    {
      type: 'imageGallery',
      layout: 'portrait',
      title: 'Sunrise and singletrack',
      intro:
        'The light that makes a 7:00 a.m. start worth it: sun on Alum Creek Lake, then dirt, roots, and a hiker moving through the trees.',
      images: [
        {
          url: SUNRISE_PHOTO,
          alt: 'Sunrise over Alum Creek Lake from a wooded shoreline during the morning hike',
          caption:
            'Lake light from the shoreline — the reason we did not sleep in. Calm water, far shore, and that first-hour sun.',
        },
        {
          url: HIKER_PHOTO,
          alt: 'Hiker in a blue shirt walking a leaf-covered dirt trail through sunlit woods at Alum Creek',
          caption:
            'On trail: dirt path, dappled sun, lake through the trees. This is the moderate footing the plan warned about — not a climb, just roots and attention.',
        },
      ],
    },
    {
      type: 'imageGallery',
      layout: 'video',
      title: 'Stream crossing on the loop',
      intro:
        'A short clip from the woods — rocky water underfoot, a yellow line across the crossing, and someone waiting on the far bank. Hit play.',
      images: [
        {
          url: TRAIL_CLIP,
          kind: 'video',
          poster: TRAIL_CLIP_POSTER,
          alt: 'Video of hikers crossing a shallow rocky stream on the Alum Creek trail',
          caption:
            'The crossing was low and clear. Poles and careful feet — then back onto dirt on the other side.',
        },
      ],
    },
    {
      type: 'table',
      title: 'Plan vs. what we actually did',
      intro: 'We used the shareable plan as the morning script. Here is how it landed.',
      headers: ['Item', 'The plan', 'What happened'],
      rows: [
        ['Meet', '7:00 a.m. at 3993 Hollenback Rd', 'On time. Group photo at the kiosk, then on trail.'],
        [
          'Route',
          'North Loop default (~6 mi, 2–2.5 hr)',
          '2.5 hours, 7:00–9:30 — the full-morning window, not the 45–60 min bailout.',
        ],
        ['Who', 'Friends group', 'Seven hikers. Poles came out; nobody treated it like a race.'],
        ['Weather / cancel', 'Postpone if last night poured', 'We went. Woods were green, the lot was dry, and the stream crossing was low.'],
        ['Done', 'Lot ~9:15–9:45 a.m.', '**9:30 a.m.** — stretch, cars, morning still intact.'],
      ],
    },
    {
      type: 'panel',
      title: 'How the morning felt',
      paragraphs: [
        'Rocks and Roots is moderate because of **footing**, not a Cemetery Ridge climb. That is exactly what we got: winding dirt, roots, a shallow rocky crossing, and a crew that slowed down instead of charging the first mile.',
        'Seven people is a good size for this trail — enough conversation, easy to keep eyes on the person in front when the singletrack twists. Two of us carried trekking poles, which helped on the uneven bits and at the water. Compression sleeves and trail shoes were the uniform. No one showed up in gym flats.',
        'The kiosk map (lake in the middle, woods around it) is the reminder we were in the right lot: **Alum Creek hiking**, not the beach and not the COMBO bike park. Once you are in the trees, the lake keeps showing up through the trunks — that sunrise shot is the same water.',
      ],
      list: [
        '**Start:** 7:00 a.m. selfie, short brief, walk.',
        '**Middle:** roots, shade, stream crossing, talk, water. Nobody needed the 45-minute turnaround.',
        '**Finish:** 9:30 a.m. back at the paved lot, still smiling in the same spot we started.',
      ],
    },
    {
      type: 'mapEmbed',
      title: 'Where we parked',
      intro:
        'Same pin as the plan: Rocks and Roots trailhead on Hollenback Road, west side of the lake.',
      maps: [
        {
          title: 'Rocks and Roots trailhead',
          address: '3993 Hollenback Rd, Lewis Center, OH 43035',
          embedUrl: osmEmbed(40.1985, -82.9575, 0.016),
          directionsUrl: googleMaps(TRAILHEAD_QUERY),
        },
      ],
    },
    {
      type: 'panel',
      title: 'What we would repeat',
      list: [
        '**7:00 a.m. is the right call.** Lot light, lake sun, done before the day fills up.',
        '**Screenshot the plan overnight.** Cell service is uneven once you leave the kiosk.',
        '**Poles help** on rocks, roots, and the stream bed. Not required, but two of us were glad to have them.',
        '**Stick together on the full loop.** The short bailout is still there if someone needs it next time.',
        '**Next step** when this feels easy: [Cemetery Ridge at Clear Creek](/blog/cemetery-ridge-clear-creek-hike-prep). Until then, this loop is the local moderate hike.',
      ],
      callout: {
        text: 'Use the 7:00 a.m. plan if you are going again →',
        url: '/blog/rocks-and-roots-alum-creek-morning-hike-plan',
      },
    },
  ],
  relatedPostsTitle: 'The plan we followed, and more Alum Creek trails',
  relatedPosts: [
    {
      slug: 'rocks-and-roots-alum-creek-morning-hike-plan',
      title: 'Rocks and Roots Morning Hike Plan (Alum Creek, 7:00 a.m.)',
      blurb: 'the meet pin, pack list, and North Loop script we actually used.',
    },
    {
      slug: 'alum-creek-best-hike-spots-beginner-moderate',
      title: 'Best Alum Creek Hike Spots (Beginner to Moderate Plan)',
      blurb: 'easier trails at the same park if someone wants a shorter next outing.',
    },
    {
      slug: 'cemetery-ridge-clear-creek-hike-prep',
      title: 'How to Prepare for Cemetery Ridge at Clear Creek Metro Park',
      blurb: 'the steeper next hike when Rocks and Roots feels normal.',
    },
    {
      slug: 'ohio-beginner-hiking-guide',
      title: 'Ohio Beginner Hiking Guide: Easy First Trails Across the State',
      blurb: 'first-hike options closer to paved paths.',
    },
  ],
  referenceLinks: [
    {
      label: 'Rocks and Roots 7:00 a.m. plan (the script we followed)',
      url: '/blog/rocks-and-roots-alum-creek-morning-hike-plan',
    },
    {
      label: 'Ohio DNR — Alum Creek State Park',
      url: 'https://ohiodnr.gov/go-and-do/plan-a-visit/find-a-property/alum-creek-state-park',
    },
    {
      label: 'Ohio DNR — Alum Creek hiking map (PDF)',
      url: 'https://dam.assets.ohio.gov/image/upload/ohiodnr.gov/documents/parks/parkmaps/alumcreektrailmap.pdf',
    },
    {
      label: 'Google Maps — Rocks and Roots trailhead (3993 Hollenback Rd)',
      url: googleMaps(TRAILHEAD_QUERY),
    },
  ],
};
